import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { CheckboxList } from '../../components/CheckboxList';
import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import {
  PATH_RESOLVABLE_MISSION_TYPE_TOOLKITS,
  PATH_RESOLVABLE_MISSION_TYPE_TOOLKITS_EDIT,
} from '../../app/constants/paths.constant';
import { FormVariant } from '../../forms';
import {
  ResolvableMissionTypeForm,
  ResolvableMissionTypeFormTab,
} from '../../forms/ResolvableMissionType';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

export const ResolvableMissionTypePage_Toolkits_ResolvableMissionType_Fragment =
  graphql(`
    fragment ResolvableMissionTypePage_Toolkits_ResolvableMissionType_Fragment on ResolvableMissionType {
      _id
      enabledToolkitIds
      ...ResolvableMissionTypeForm_ResolvableMissionType_Fragment
    }
  `);

interface Props {
  readonly resolvableMissionType: FragmentType<
    typeof ResolvableMissionTypePage_Toolkits_ResolvableMissionType_Fragment
  >;
}

export const Toolkits = ({
  resolvableMissionType: pResolvableMissionType,
}: Readonly<Props>) => {
  const resolvableMissionType = getFragmentData(
    ResolvableMissionTypePage_Toolkits_ResolvableMissionType_Fragment,
    pResolvableMissionType,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_RESOLVABLE_MISSION_TYPE_TOOLKITS,
      edit: PATH_RESOLVABLE_MISSION_TYPE_TOOLKITS_EDIT,
    },
    { id: resolvableMissionType._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { toolkits } = useReferencesContext();

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <Paper sx={{ p: 2 }}>
          <Typography gutterBottom variant="h6">
            Ensemble d&apos;outils activés
          </Typography>
          <CheckboxList<Id>
            idPrefix="mission-type-toolkits-"
            items={toolkits.map((toolkit) => ({
              id: toolkit._id,
              label: toolkit.label,
            }))}
            value={resolvableMissionType.enabledToolkitIds ?? EMPTY_ARRAY}
            variant="consultation"
          />
        </Paper>
      </Stack>
      <SpeedDial
        actions={[
          {
            id: 'editMissionTypeToolkitsParameters',
            icon: <EditIcon />,
            name: 'Modifier Paramétrage Outils',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur le Paramétrage des Outils du Type de Mission"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <ResolvableMissionTypeForm
          formVariant={FormVariant.Modification}
          initialValues={resolvableMissionType}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          resolvableMissionTypeId={resolvableMissionType._id}
          stepIdsSubset={[ResolvableMissionTypeFormTab.Toolkits]}
          title="Formulaire de modification du Paramétrage des Outils du Type de Mission"
        />
      </RightDrawer>
    </>
  );
};
